import { ChangeEvent, useEffect, useState } from "react";
import { Box, TableCell, TableRow, TextField, Typography, Tooltip, Checkbox } from "@mui/material";
import { FiberManualRecord, Error } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { colorByDeliveryStatus } from "../functions/colorByDeliveryStatus.function";
import { ServiceData } from "../ScheduleSelection-Tab-Services.component";
import {
  NoPlaceZoneBooking,
  ServiceDeliveryStatus
} from "../../../../../features/Booking-Form/typings/booking-service-administration.types";
import { MaxSetUpTime } from "../../../../../features/Booking-Form/typings/booking-services.types";
import { sortDataByOrdered } from "../functions/sortDataByOrdered.function";
import { renderServiceImage } from "../../../../FacilityManager/Components/Toolbars/InfoSidebar/ZoneInfoSidebar/ServicePartial/ServiceCommonRender.partial";
import { EnlargedImageDialog } from "../../../../../features/EnlargedImageCarousel/enlarged-image-dialog.component";
import { BookingRequestsStatus } from "../../../../BookingRequest/typings/booking-requests.types";

export function ServiceDataTableRows({
  isEditable,
  isColleagueScheduleEditable,
  showQty,
  rowData,
  schedule,
  maxSetupTime,
  handleChangeQty,
  handleSelectOrder
}: Readonly<{
  isEditable: boolean;
  isColleagueScheduleEditable: boolean;
  showQty: boolean;
  rowData: ServiceData[];
  schedule?: NoPlaceZoneBooking;
  maxSetupTime?: MaxSetUpTime;
  handleChangeQty(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, item: ServiceData): void;
  handleSelectOrder(item: ServiceData): void;
}>) {
  const { t } = useTranslation();

  const [entries, setEntries] = useState<ServiceData[]>([]);
  const [showBiggerImage, setShowBiggerImage] = useState<string | null>(null);

  // Validation function to check if ordered quantity exceeds available quantity
  const validateQuantity = (data: ServiceData) => {
    // If availableQty is null, no validation needed
    if (data.availableQty === null) return { isValid: true, errorMessage: "" };

    // If orderedQty exceeds availableQty, show error
    if (data.orderedQty > data.availableQty)
      return { isValid: false, errorMessage: t("Order quantity exceeds available amount") };

    return { isValid: true, errorMessage: "" };
  };

  // handle the order selection
  const handleOnChange = (e: ChangeEvent<HTMLInputElement>, selectedData: ServiceData) => {
    const newCheck = e.target.checked;
    const index = entries.findIndex(item => item.id === selectedData.id);

    const updated = [...entries];
    updated[index] = {
      ...updated[index],
      ordered: newCheck,
      status: newCheck ? BookingRequestsStatus.REQUESTED : null,
      deliveryStatus: newCheck ? ServiceDeliveryStatus.ORDERED : null,
      qty: newCheck ? 1 : 0,
      orderedQty: newCheck ? 1 : 0 // ✅ update orderedQty
    };

    setEntries(sortDataByOrdered(updated));

    // send updated data upward
    handleSelectOrder({ ...updated[index] });
  };

  const compareBookingTime = DateTime.fromISO(schedule?.startDate || DateTime.now().toISO())
    .diffNow()
    .as("hours");

  const isQtyEditable = (data: ServiceData) =>
    compareBookingTime > (data.bookingLeadTime ?? 1) &&
    data.setUpTime <= (maxSetupTime?.maxSetUpTime ?? 100000000);

  useEffect(() => {
    const sortedRowData = sortDataByOrdered(rowData);
    setEntries(sortedRowData);
  }, [rowData]);

  const unorderedQtyText = (data: ServiceData) => (data.ordered ? t("_unsetQty") : "");
  const deliveryQtyText = (data: ServiceData) =>
    data.availableQty ? Math.min(data.orderedQty, data.availableQty) : unorderedQtyText(data);

  return (
    <>
      {rowData.length ? (
        entries.map(data => (
          <TableRow key={data.id} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
            {/* ordered switch */}
            <TableCell scope="row" width={40}>
              <Checkbox
                checked={data.ordered}
                disabled={!data.editable}
                onChange={e => handleOnChange(e, data)}
                data-testid={`schedule-selection-tab-services-dataTable-checkbox${data.id}`}
              />
            </TableCell>

            {/* name */}
            <TableCell scope="row" width={200}>
              <Typography variant={"body2"}>{data.name}</Typography>
            </TableCell>

            {/* image */}
            <TableCell scope="row" width={200}>
              <Typography variant={"body2"}>
                {renderServiceImage(80, data.picturePath, setShowBiggerImage)}
              </Typography>
            </TableCell>

            {/* status */}
            <TableCell align="left" width={180}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {data.status && (
                  <>
                    <FiberManualRecord sx={{ color: colorByDeliveryStatus(data.deliveryStatus) }} />
                    <Typography variant={"body2"}>{t(data.status)}</Typography>
                  </>
                )}
              </Box>
            </TableCell>

            {/* quantity */}
            {showQty && (
              <>
                {/* Delivery Qty */}
                <TableCell align="right" width={140}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography sx={{ m: "0 auto" }}>{deliveryQtyText(data)}</Typography>
                  </Box>
                </TableCell>

                {/* Ordered Qty */}
                <TableCell align="right" width={140}>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    {isEditable || isColleagueScheduleEditable ? (
                      <>
                        {data.ordered && (
                          <TextField
                            data-testid={`booking-service-data-row-qty-field${data.id}`}
                            value={data.orderedQty}
                            sx={{ width: 60 }}
                            disabled={!isQtyEditable(data) || !data.ordered || !data.editable}
                            type="number"
                            onChange={e => handleChangeQty(e, data)}
                            error={!validateQuantity(data).isValid}
                            title="" // Disable the default tooltip
                            slotProps={{
                              htmlInput: {
                                step: 1,
                                min: 1,
                                max: data.availableQty ?? undefined,
                                style: { textAlign: "center" }
                              },
                              input: {
                                endAdornment: validateQuantity(data).isValid ? null : (
                                  <Tooltip
                                    title={validateQuantity(data).errorMessage}
                                    placement="top"
                                    arrow
                                  >
                                    <Error
                                      data-testid="service-item-qty-field-error"
                                      color="error"
                                      sx={{ fontSize: "1rem", cursor: "pointer" }}
                                    />
                                  </Tooltip>
                                )
                              }
                            }}
                          />
                        )}
                      </>
                    ) : (
                      <Typography sx={{ m: "0 auto" }}>{data.orderedQty}</Typography>
                    )}
                  </Box>
                </TableCell>

                {/* Available Qty */}
                <TableCell align="right" width={140}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography sx={{ m: "0 auto" }}>
                      {data.availableQty ?? unorderedQtyText(data)}
                    </Typography>
                  </Box>
                </TableCell>
              </>
            )}
          </TableRow>
        ))
      ) : (
        <TableRow>
          <TableCell colSpan={6} align="center">
            <Typography variant="body2">{t("No Options")}</Typography>
          </TableCell>
        </TableRow>
      )}

      <EnlargedImageDialog
        open={!!showBiggerImage}
        picturePath={showBiggerImage}
        handleClose={() => setShowBiggerImage(null)}
      />
    </>
  );
}
